<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CreateTicketDialog from './components/CreateTicketDialog.vue';
import TicketDetail from './components/TicketDetail.vue';

const { t } = useI18n();
const store = useStore();
const alert = useAlert();

const tickets = ref([]);
const loading = ref(false);
const searchQuery = ref('');
const statusFilter = ref('all');
const showCreateDialog = ref(false);
const selectedTicket = ref(null);
const currentPage = ref(1);
const perPage = ref(20);

// 从 store 获取分页元数据
const meta = computed(() => store.getters['supportTickets/getMeta']);

// 计算可见页码
const visiblePages = computed(() => {
  const total = meta.value.total_pages || 1;
  const current = currentPage.value;
  const delta = 2; // 当前页前后显示的页码数

  const range = [];
  const rangeWithDots = [];
  let l;

  range.push(1);

  for (
    let i = Math.max(2, current - delta);
    i <= Math.min(total - 1, current + delta);
    i += 1
  ) {
    range.push(i);
  }

  range.push(total);

  for (const i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push('...');
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
});

// 移除客户端筛选，改用服务端分页
const fetchTickets = async (page = 1) => {
  loading.value = true;
  try {
    const params = {
      page,
      per_page: perPage.value,
    };

    // 添加状态筛选
    if (statusFilter.value !== 'all') {
      params.status = statusFilter.value;
    }

    // 添加搜索
    if (searchQuery.value.trim()) {
      params.q = searchQuery.value.trim();
    }

    const response = await store.dispatch('supportTickets/get', params);
    tickets.value = response.tickets || [];
    currentPage.value = meta.value.current_page || 1;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch tickets:', error);
  } finally {
    loading.value = false;
  }
};

// 监听搜索和筛选变化
watch([searchQuery, statusFilter], () => {
  fetchTickets(1);
});

// 分页导航
const goToPage = page => {
  if (page < 1 || page > (meta.value.total_pages || 1)) return;
  fetchTickets(page);
};

const goToPrevPage = () => {
  if (currentPage.value > 1) {
    fetchTickets(currentPage.value - 1);
  }
};

const goToNextPage = () => {
  if (currentPage.value < (meta.value.total_pages || 1)) {
    fetchTickets(currentPage.value + 1);
  }
};

const statusOptions = computed(() => [
  { label: t('SUPPORT_TICKETS.STATUS.ALL'), value: 'all' },
  { label: t('SUPPORT_TICKETS.STATUS.OPEN'), value: '0' },
  { label: t('SUPPORT_TICKETS.STATUS.IN_PROGRESS'), value: '1' },
  { label: t('SUPPORT_TICKETS.STATUS.RESOLVED'), value: '2' },
  { label: t('SUPPORT_TICKETS.STATUS.CLOSED'), value: '3' },
]);

const tableHeaders = computed(() => [
  t('SUPPORT_TICKETS.TABLE_HEADER.NUMBER'),
  t('SUPPORT_TICKETS.TABLE_HEADER.TITLE'),
  t('SUPPORT_TICKETS.LABELS.PRIORITY'),
  t('SUPPORT_TICKETS.TABLE_HEADER.STATUS'),
  t('SUPPORT_TICKETS.TABLE_HEADER.CREATED_AT'),
]);

const getStatusLabel = status => {
  const statusMap = {
    0: 'OPEN',
    1: 'IN_PROGRESS',
    2: 'RESOLVED',
    3: 'CLOSED',
  };
  return t(`SUPPORT_TICKETS.STATUS.${statusMap[status] || 'OPEN'}`);
};

const getStatusColor = status => {
  const colorMap = {
    0: 'text-n-amber-11 bg-n-amber-2',
    1: 'text-n-blue-11 bg-n-blue-2',
    2: 'text-n-green-11 bg-n-green-2',
    3: 'text-n-slate-11 bg-n-slate-3',
  };
  return colorMap[status] || colorMap[0];
};

const getPriorityLabel = priority => {
  const labels = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
  return t(`SUPPORT_TICKETS.PRIORITY.${labels[priority] || 'MEDIUM'}`);
};

const getPriorityColor = priority => {
  const colorMap = {
    0: 'text-n-green-11 bg-n-green-2',
    1: 'text-n-blue-11 bg-n-blue-2',
    2: 'text-n-orange-11 bg-n-orange-2',
    3: 'text-n-red-11 bg-n-red-2',
  };
  return colorMap[priority] || colorMap[1];
};

const formatDate = dateString => {
  return new Date(dateString).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const handleCreateTicket = async ticketData => {
  try {
    await store.dispatch('supportTickets/create', ticketData);
    useAlert(t('SUPPORT_TICKETS.CREATE_SUCCESS'));
    // 先关闭对话框，确保 UI 立即响应
    showCreateDialog.value = false;
    await nextTick();
    // 再刷新列表（不等待结果，避免影响对话框关闭）
    fetchTickets(currentPage.value);
  } catch (error) {
    useAlert(t('SUPPORT_TICKETS.CREATE_ERROR'));
    showCreateDialog.value = false;
  }
};

const handleTicketClick = ticket => {
  selectedTicket.value = ticket;
};

const handleBackToList = () => {
  selectedTicket.value = null;
};

const handleCloseTicket = async ticketId => {
  try {
    await store.dispatch('supportTickets/close', ticketId);
    useAlert(t('SUPPORT_TICKETS.CLOSE_SUCCESS'));
    await fetchTickets(currentPage.value);
  } catch (error) {
    useAlert(t('SUPPORT_TICKETS.CLOSE_ERROR'));
  }
};

const handleUrgeTicket = async ticketId => {
  try {
    await store.dispatch('supportTickets/urge', ticketId);
    useAlert(t('SUPPORT_TICKETS.URGE_SUCCESS'));
    await fetchTickets(currentPage.value);
  } catch (error) {
    useAlert(t('SUPPORT_TICKETS.URGE_ERROR'));
  }
};

const hideCreateDialog = () => {
  showCreateDialog.value = false;
};

onMounted(() => {
  fetchTickets();
});
</script>

<template>
  <!-- 工单详情视图 -->
  <TicketDetail
    v-if="selectedTicket"
    :key="selectedTicket.id"
    :ticket="selectedTicket"
    @back="handleBackToList"
    @close="handleCloseTicket"
  />

  <!-- 工单列表视图 -->
  <div v-else class="flex flex-col w-full h-full gap-8">
    <!-- 头部 -->
    <BaseSettingsHeader
      :title="t('SUPPORT_TICKETS.HEADER')"
      :description="t('SUPPORT_TICKETS.DESCRIPTION')"
      icon-name="clipboard"
      feature-name="support_tickets"
    >
      <template #actions>
        <Button
          icon="i-lucide-plus"
          :label="t('SUPPORT_TICKETS.CREATE_BUTTON')"
          @click="showCreateDialog = true"
        />
      </template>
    </BaseSettingsHeader>

    <!-- 主内容区域 -->
    <main class="flex-1 overflow-auto">
      <!-- 加载状态 -->
      <div v-if="loading" class="flex items-center justify-center py-20">
        <woot-loading-state :message="t('SUPPORT_TICKETS.LOADING')" />
      </div>

      <!-- 空状态 -->
      <div
        v-else-if="tickets.length === 0"
        class="flex-1 py-20 text-n-slate-12 flex items-center justify-center text-base"
      >
        {{ t('SUPPORT_TICKETS.EMPTY') }}
      </div>

      <!-- 工单列表内容 -->
      <div v-else class="space-y-6">
        <!-- 搜索和筛选 -->
        <div class="flex items-center gap-3 mb-6">
          <div class="relative flex-1 max-w-md">
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="t('SUPPORT_TICKETS.SEARCH_PLACEHOLDER')"
              class="w-full px-4 py-2 pl-10 text-sm border rounded-lg bg-n-solid-1 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
            />
            <span
              class="absolute left-3 top-1/2 -translate-y-1/2 text-n-slate-10"
            >
              <span class="icon-search-line" />
            </span>
          </div>

          <select
            v-model="statusFilter"
            class="px-4 py-2 text-sm border rounded-lg bg-n-solid-1 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
          >
            <option
              v-for="option in statusOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- 工单列表表格 -->
        <div
          class="overflow-hidden border rounded-lg bg-n-solid-1 border-n-weak"
        >
          <table class="min-w-full divide-y divide-n-weak">
            <thead class="bg-n-slate-2">
              <tr>
                <th
                  v-for="header in tableHeaders"
                  :key="header"
                  class="px-6 py-3 text-xs font-semibold tracking-wider text-left uppercase text-n-slate-11"
                >
                  {{ header }}
                </th>
                <th
                  class="px-6 py-3 text-xs font-semibold tracking-wider text-right uppercase text-n-slate-11"
                >
                  {{ t('SUPPORT_TICKETS.TABLE_HEADER.ACTIONS') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="ticket in tickets"
                :key="ticket.id"
                class="transition-colors cursor-pointer hover:bg-n-slate-2"
                @click="handleTicketClick(ticket)"
              >
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="font-mono text-sm font-medium text-n-slate-12">
                    #{{ ticket.ticket_no }}
                  </span>
                </td>
                <td class="px-6 py-4">
                  <div class="max-w-md">
                    <p class="text-sm font-medium text-n-slate-12 truncate">
                      {{ ticket.title }}
                    </p>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    class="inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full"
                    :class="[
                      getPriorityColor(ticket.priority)
                    ]"
                  >
                    {{ getPriorityLabel(ticket.priority) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    class="inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full"
:class="[
                      getStatusColor(ticket.status),
                    ]"
                  >
                    {{ getStatusLabel(ticket.status) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-n-slate-11">
                  {{ formatDate(ticket.created_at) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right">
                  <Button
                    v-if="ticket.can_urge"
                    size="sm"
                    variant="ghost"
                    icon="i-lucide-bell"
                    :label="t('SUPPORT_TICKETS.BUTTONS.URGE')"
                    @click.stop="handleUrgeTicket(ticket.id)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 分页控件 -->
        <div
          v-if="meta.total_count > 0"
          class="flex flex-col sm:flex-row items-center justify-between mt-6 gap-4"
        >
          <div class="text-sm text-n-slate-11">
            共 {{ meta.total_count }} 条工单，第 {{ meta.current_page }} /
            {{ meta.total_pages }} 页
          </div>

          <div class="flex items-center gap-2">
            <!-- 上一页 -->
            <Button
              variant="outline"
              size="sm"
              :disabled="meta.current_page === 1"
              @click="goToPrevPage"
            >
              <span class="icon-arrow-left-line mr-1" />
              {{ t('SUPPORT_TICKETS.PAGINATION.PREV') }}
            </Button>

            <!-- 页码 -->
            <div class="hidden sm:flex items-center gap-1">
              <button
                v-for="page in visiblePages"
                :key="page"
                class="px-3 py-1 text-sm rounded-md transition-colors"
:class="[
                  page === currentPage
                    ? 'bg-n-brand text-white'
                    : page === '...'
                      ? 'text-n-slate-11 cursor-default'
                      : 'text-n-slate-12 hover:bg-n-slate-2',
                ]"
                :disabled="page === '...'"
                @click="page !== '...' && goToPage(page)"
              >
                {{ page }}
              </button>
            </div>

            <!-- 下一页 -->
            <Button
              variant="outline"
              size="sm"
              :disabled="meta.current_page === meta.total_pages"
              @click="goToNextPage"
            >
              {{ t('SUPPORT_TICKETS.PAGINATION.NEXT') }}
              <span class="icon-arrow-right-line ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </main>
  </div>

  <!-- 创建工单弹窗 -->
  <woot-modal
    v-model:show="showCreateDialog"
    size="medium"
    :on-close="hideCreateDialog"
  >
    <CreateTicketDialog
      v-if="showCreateDialog"
      :on-close="hideCreateDialog"
      @submit="handleCreateTicket"
    />
  </woot-modal>
</template>
